"use client"

import * as React from "react"
import {
  FloatingSheetPanel,
  FloatingSheetPanelBody,
  FloatingSheetPanelContent,
  FloatingSheetPanelHeader,
  FloatingSheetPanelToolbar,
} from "@/lib/floating-sheet-panel"
import { OperationsTermTable } from "@/components/evaluation-dashboard/operations/operations-term-table"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"
import {
  buildWorkQueueDetailRows,
  type EvaluationWorkQueueRow,
} from "@/lib/mock/evaluation-work-queue"

interface OperationsTermWorkQueueSheetProps {
  term: EvaluationTerm
  /** Summary rows from the compact list — used for prev/next in the toolbar. */
  summaryRows: EvaluationWorkQueueRow[]
  focusRow: EvaluationWorkQueueRow | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onFocusRowChange: (row: EvaluationWorkQueueRow) => void
}

export function OperationsTermWorkQueueSheet({
  term,
  summaryRows,
  focusRow,
  open,
  onOpenChange,
  onFocusRowChange,
}: OperationsTermWorkQueueSheetProps) {
  const [shown, setShown] = React.useState(focusRow)
  if (focusRow && focusRow !== shown) setShown(focusRow)

  const detailRows = React.useMemo(
    () => (shown ? buildWorkQueueDetailRows(term, shown.state) : []),
    [shown, term],
  )

  const shownIndex = shown ? summaryRows.findIndex(row => row.id === shown.id) : -1

  return (
    <FloatingSheetPanel open={open} onOpenChange={onOpenChange}>
      <FloatingSheetPanelContent size="sm" contentSlot="eval-operations-work-queue">
        <FloatingSheetPanelToolbar
          onPrevious={
            shownIndex > 0
              ? () => onFocusRowChange(summaryRows[shownIndex - 1]!)
              : undefined
          }
          onNext={
            shownIndex >= 0 && shownIndex < summaryRows.length - 1
              ? () => onFocusRowChange(summaryRows[shownIndex + 1]!)
              : undefined
          }
          previousLabel="Previous queue"
          nextLabel="Next queue"
        />
        <FloatingSheetPanelHeader
          title={shown?.actionLabel ?? "Work queue"}
          subtitle={`${term.termName} · ${term.academicYear}`}
          description={
            shown && detailRows.length > 0
              ? `${detailRows.length} ${detailRows.length === 1 ? "item" : "items"}`
              : undefined
          }
        />
        <FloatingSheetPanelBody className="px-4 pb-4">
          {shown ? (
            <OperationsTermTable termId={term.id} rows={detailRows} />
          ) : null}
        </FloatingSheetPanelBody>
      </FloatingSheetPanelContent>
    </FloatingSheetPanel>
  )
}
